import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button } from 'antd';
import styled from 'styled-components';
import API from '../../../utils/api';
import { MEDIA_URL } from '../../../app/constants/APIKEYS';
import dayjs from 'dayjs';

type LandingPopupItem = {
  popupId: number;
  title: string | null;
  body: string | null;
  imageUrl: string | null;
  linkUrl: string | null;
  linkLabel: string | null;
  showFrom?: string | null;
  showUntil?: string | null;
};

const DISMISS_KEY = 'landingPopupDismissUntil';
type DismissedMap = Record<string, number>;
const OPTIONAL_TEXT_PLACEHOLDER = '\u200B'; // synced with admin popup placeholder for optional text
const cleanseOptionalText = (value?: string | null) => {
  if (typeof value !== 'string') return '';
  return value.split(OPTIONAL_TEXT_PLACEHOLDER).join('').trim();
};

const resolveImageUrl = (url: string | null): string | null => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${MEDIA_URL.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
};

const persistDismissedMap = (map: DismissedMap) => {
  if (typeof window === 'undefined') return;
  if (Object.keys(map).length === 0) {
    window.localStorage.removeItem(DISMISS_KEY);
    return;
  }
  window.localStorage.setItem(DISMISS_KEY, JSON.stringify(map));
};

const sanitizeDismissedMap = (map: unknown): DismissedMap => {
  const now = Date.now();
  if (!map || typeof map !== 'object') return {};
  return Object.entries(map as DismissedMap).reduce<DismissedMap>((acc, [key, value]) => {
    const expiresAt = typeof value === 'number' ? value : Number(value);
    if (!Number.isNaN(expiresAt) && expiresAt > now) {
      acc[key] = expiresAt;
    }
    return acc;
  }, {});
};

const loadDismissedMap = (): DismissedMap => {
  if (typeof window === 'undefined') return {};
  const rawValue = window.localStorage.getItem(DISMISS_KEY);
  if (!rawValue) return {};
  try {
    const parsed = JSON.parse(rawValue);
    const cleaned = sanitizeDismissedMap(parsed);
    if (Object.keys(cleaned).length !== Object.keys(parsed || {}).length) {
      persistDismissedMap(cleaned);
    }
    return cleaned;
  } catch (error) {
    window.localStorage.removeItem(DISMISS_KEY);
    return {};
  }
};

const isPopupDismissed = (popupId: number, map: DismissedMap) => {
  const expiresAt = map[String(popupId)];
  return typeof expiresAt === 'number' && expiresAt > Date.now();
};

const LandingPopup: React.FC = () => {
  const [popups, setPopups] = useState<LandingPopupItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [dismissedMap, setDismissedMap] = useState<DismissedMap>(() => loadDismissedMap());
  const dismissedMapRef = useRef(dismissedMap);

  useEffect(() => {
    dismissedMapRef.current = dismissedMap;
  }, [dismissedMap]);

  useEffect(() => {
    let mounted = true;
    const fetchPopups = async () => {
      try {
        const response = await API.getPublic('/popup/landing');
        const list = response?.result?.result?.list ?? [];
        if (!mounted) return;

        if (Array.isArray(list)) {
          const filtered = list.filter((popup) => !isPopupDismissed(popup.popupId, dismissedMapRef.current));
          setPopups(filtered);
          setCurrentIndex(0);
          setVisible(filtered.length > 0);
        } else {
          setPopups([]);
          setVisible(false);
        }
      } catch (error) {
        console.error('Failed to load landing popups:', error);
      }
    };
    fetchPopups();
    return () => {
      mounted = false;
    };
  }, []);

  const currentPopup = popups[currentIndex];
  const hasMultiple = popups.length > 1;
  const popupTitle = cleanseOptionalText(currentPopup?.title);
  const popupBody = cleanseOptionalText(currentPopup?.body);
  const shouldShowTitle = Boolean(popupTitle) || hasMultiple;

  const handleClose = useCallback(() => {
    setVisible(false);
  }, []);

  const handleDismissToday = useCallback(() => {
    if (!currentPopup) return;
    const targetId = currentPopup.popupId;
    const expiresAt = dayjs().endOf('day').valueOf();

    setDismissedMap((prev) => {
      const next = { ...prev, [String(targetId)]: expiresAt };
      persistDismissedMap(next);
      return next;
    });

    setPopups((prev) => {
      const next = prev.filter((popup) => popup.popupId !== targetId);
      if (next.length === 0) {
        setVisible(false);
      }
      setCurrentIndex((prevIndex) => {
        if (next.length === 0) return 0;
        return Math.min(prevIndex, next.length - 1);
      });
      return next;
    });
  }, [currentPopup]);

  const footerContent = useMemo(() => {
    if (!currentPopup) return null;

    const rightButtons: React.ReactNode[] = [];

    if (hasMultiple) {
      rightButtons.push(
        <Button key="prev" onClick={() => setCurrentIndex((prev) => (prev - 1 + popups.length) % popups.length)}>
          이전
        </Button>
      );
    }

    if (currentPopup.linkUrl) {
      rightButtons.push(
        <Button
          key="link"
          type="primary"
          onClick={() => window.open(currentPopup.linkUrl || '#', '_blank', 'noopener,noreferrer')}
        >
          {currentPopup.linkLabel || '자세히 보기'}
        </Button>
      );
    }

    rightButtons.push(
      <Button key="close" onClick={handleClose}>
        닫기
      </Button>
    );

    if (hasMultiple) {
      rightButtons.push(
        <Button
          key="next"
          type="primary"
          onClick={() => setCurrentIndex((prev) => (prev + 1) % popups.length)}
        >
          다음
        </Button>
      );
    }

    return (
      <FooterWrapper>
        <LeftActions>
          <Button key="dismissToday" onClick={handleDismissToday}>
            오늘 하루 이 창 열지 않기
          </Button>
        </LeftActions>
        <RightActions>
          {rightButtons}
        </RightActions>
      </FooterWrapper>
    );
  }, [currentPopup, handleClose, handleDismissToday, hasMultiple, popups.length]);

  if (!visible || !currentPopup) return null;

  const imageUrl = resolveImageUrl(currentPopup.imageUrl);
  const modalTitle = shouldShowTitle ? (
    <TitleWrap>
      {popupTitle && <span>{popupTitle}</span>}
      {hasMultiple && (
        <Pager>{`${currentIndex + 1} / ${popups.length}`}</Pager>
      )}
    </TitleWrap>
  ) : null;

  return (
    <Modal
      title={modalTitle}
      open={visible}
      footer={footerContent}
      onCancel={handleClose}
      maskClosable={false}
      destroyOnClose
    >
      <PopupBody>
        {imageUrl && (
          <ImageWrap>
            <img src={imageUrl} alt={popupTitle || 'popup'} />
          </ImageWrap>
        )}
        {popupBody && <ContentText>{popupBody}</ContentText>}
      </PopupBody>
    </Modal>
  );
};

export default LandingPopup;

const PopupBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ImageWrap = styled.div`
  width: 100%;
  max-height: 320px;
  overflow: hidden;
  border-radius: 8px;
  background: #f5f5f5;

  img {
    width: 100%;
    display: block;
    object-fit: contain;
  }
`;

const ContentText = styled.div`
  white-space: pre-wrap;
  color: #202123;
  font-size: 15px;
  line-height: 1.6;
`;

const Pager = styled.span`
  font-size: 13px;
  color: #6f6f6f;
  margin-left: 12px;
`;

const TitleWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FooterWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const LeftActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const RightActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
`;
